import { InfoGatheringAPI } from './API'
import {
  GetChecklistRequest,
  ProvideSupportingItemNotesRequest,
  RequestedSupportingItemUpdatedRealtimeEvent,
} from '@taxfyle/api-internal/internal/info_gathering_pb'
import { Id } from '../types/definitions'
import { collection } from '../infra/collection'
import Checklist from './Checklist'
import { ChecklistDTO } from './dtos/info-gathering'
import {
  mapChecklistProtoToDTO,
  mapSupportingItemToDto,
} from './dtos/checklistGrpcDtoMapper'
import { Observable } from 'rxjs'
import { filter } from 'rxjs/operators'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import TypeCase = RealtimeEvent.TypeCase
import { maybeParseDate } from '../utils/date-util'
import { PrimeDocument } from '../documents/DocumentStore'
import { PrimeDocumentTypeByShortId } from '../documents/DocumentTypeStore'

export default class InfoGatheringStore {
  /**
   * Checklist collection.
   */
  checklists = collection<ChecklistDTO, Checklist>({
    create: () => new Checklist(this.primeDocument, this.primeDocumentType),
    idAttribute: 'job_id',
  })

  constructor(
    private api: InfoGatheringAPI,
    private events$: Observable<RealtimeEvent>,
    private readonly primeDocument: PrimeDocument,
    private readonly primeDocumentType: PrimeDocumentTypeByShortId,
  ) {
    this.events$
      .pipe(
        filter((event) => {
          return (
            event.getTypeCase() ===
              TypeCase.REQUESTED_SUPPORTING_ITEM_UPDATED ||
            event.getTypeCase() === TypeCase.DOCUMENT_REQUEST_CREATED
          )
        }),
      )
      .subscribe((e) => this._onRemoteItemUpdated(e))
  }

  async getChecklist(jobId: Id) {
    const id = Id.assert(jobId)
    const request = new GetChecklistRequest().setJobId(id)
    return this.api
      .getChecklist(request)
      .then((response) => mapChecklistProtoToDTO(response, id))
      .then((checklist) => this.checklists.set(checklist))
  }

  async provideSupportingItemNotes({
    jobId,
    itemId,
    notes,
  }: {
    jobId: Id
    itemId: Id
    notes: string
  }) {
    const request = new ProvideSupportingItemNotesRequest()
      .setJobId(Id.assert(jobId))
      .setId(Id.assert(itemId))
      .setNotes(notes)
    await this.api.provideSupportingItemNotes(request)
    await this.getChecklist(jobId)
  }

  /**
   * Maps the proto message to an event type.
   *
   * @param e
   * @private
   */
  private _onRemoteItemUpdated(e: RealtimeEvent) {
    switch (e.getTypeCase()) {
      case TypeCase.REQUESTED_SUPPORTING_ITEM_UPDATED:
        this.updateRequestedItem(e.getRequestedSupportingItemUpdated())
        break
      case TypeCase.DOCUMENT_REQUEST_CREATED: {
        const jobId = e.getDocumentRequestCreated()?.getJobId()
        if (jobId) {
          this.getChecklist(jobId)
        }
        break
      }
    }
  }

  /**
   * Updates the requested supporting item from an event.
   *
   * @param event
   * @returns
   */
  private updateRequestedItem(
    event?: RequestedSupportingItemUpdatedRealtimeEvent,
  ) {
    const jobId = event?.getJobId()
    const protoDTO = event?.getRequestedSupportingItem()
    if (!protoDTO) {
      return
    }
    const itemDto = mapSupportingItemToDto(protoDTO)

    const checklist = this.checklists.get(jobId)

    if (!checklist) {
      return
    }

    const item = checklist.getItemById(itemDto.id)

    if (!item) {
      return
    }

    // Only update if the server timestamp is more recent.
    const lastUpdated = maybeParseDate(itemDto.update_time)
    if (!lastUpdated) {
      return
    }
    if (!item.dateModified || item.dateModified < lastUpdated) {
      item.set(itemDto, {
        parse: true,
        stripUndefined: true,
      })
    }
  }
}
